import { useEffect, useRef, useState } from 'react'
import { getLookups } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { BrowserUtility } from 'utility/browser-utility'
import { StorageConstant } from 'utility/constant/common'

/** @typedef {import('utility/crm/types').CrmLookups} CrmLookups */

// Lookups are cached per signed-in user and company (one call, shared by every component). Logout / login do not
// reload the page, so the cache is keyed on the user: another user in the same tab never sees the previous
// company's users or templates.
/** @type {Promise<CrmLookups>|null} */
let lookupsPromise = null
/** @type {CrmLookups|null} */
let lookupsValue = null
/** @type {string|null} */
let lookupsKey = null

/** "userId:companyId" of the signed-in user ('' when nobody is signed in). */
export const currentLookupsKey = () => {
    try {
        const user = BrowserUtility.getObj(StorageConstant.user)
        return user ? `${user.unique_id || 0}:${user.CompanyId || 0}` : ''
    } catch {
        return '' // storage unreadable: '' is its own key, so a cache filled before this is dropped
    }
}

/** Drop the cache (used by the retry button and when the signed-in user changes). */
export const resetCrmLookups = () => {
    lookupsPromise = null
    lookupsValue = null
    lookupsKey = null
}

/** Clears the cache when it belongs to another user / company than the one signed in now. */
const syncLookupsKey = () => {
    const key = currentLookupsKey()
    if (key !== lookupsKey) {
        resetCrmLookups()
        lookupsKey = key
    }
    return key
}

/** Cached lookups of the signed-in user, or null (for the first render). */
export const cachedCrmLookups = () => {
    syncLookupsKey()
    return lookupsValue
}

export const loadLookups = () => {
    const key = syncLookupsKey()
    if (!lookupsPromise) {
        const promise = getLookups()
            .then(value => {
                // A late answer for a user who has since signed out must not fill the next user's cache.
                if (lookupsKey === key) lookupsValue = value
                return value
            })
            .catch(err => {
                if (lookupsPromise === promise) lookupsPromise = null // allow a retry
                throw err
            })
        lookupsPromise = promise
    }
    return lookupsPromise
}

/** @returns {{ lookups: CrmLookups|null, loading: boolean, error: import('utility/crm/types').CrmApiError|null, retry: () => void }} */
export const useCrmLookups = () => {
    const [state, setState] = useState(() => {
        const cached = cachedCrmLookups()
        return { lookups: cached, loading: !cached, error: null }
    })
    const mounted = useRef(true)

    const run = () => {
        setState(prev => ({ ...prev, loading: !cachedCrmLookups(), error: null }))
        loadLookups()
            .then(lookups => mounted.current && setState({ lookups, loading: false, error: null }))
            .catch(err => mounted.current && setState({ lookups: null, loading: false, error: toApiError(err) }))
    }

    useEffect(() => {
        mounted.current = true
        if (!cachedCrmLookups()) run()
        // A late answer after the screen is gone must not update state.
        return () => {
            mounted.current = false
        }
    }, [])

    return { ...state, retry: run }
}
